/**
 * Session-scoped suppression for announcement modals and the chat survey.
 *
 * Two jobs: the once-per-session frequency (which the server cannot observe),
 * and stopping a just-dismissed modal flashing back before the `active` query
 * refetches.
 *
 * Keyed by the **view id** (`announcement:…` / `config:…` / `chat-survey:…`) so all
 * sources share one mechanism, and by **user id** as well, matching `useInAppNotifications`:
 * `sessionStorage` is per tab, not per account, so a shared-machine
 * logout/login in the same tab would otherwise inherit the previous user's
 * dismissals and hide an announcement the new user has never seen.
 */

/**
 * The `notification` in the key predates the rename to "announcement" and is
 * kept deliberately: it is live in users' `sessionStorage`, and changing it
 * would re-show every announcement already dismissed in an open tab.
 */
const keyFor = (viewId: string, userId: string): string =>
  `evergpt-notification-session-dismissed:${viewId}:${userId}`

/**
 * Without a user id there is nothing safe to key on, so the guard reports "not
 * dismissed" and lets the server's own dismissal state decide. Showing a
 * announcement twice is a far cheaper failure than hiding one entirely.
 */
export const isSessionDismissed = (
  viewId: string,
  userId: string | null | undefined,
): boolean => {
  if (!userId) return false
  try {
    return !!sessionStorage.getItem(keyFor(viewId, userId))
  } catch {
    // Private mode / sandboxed iframe.
    return false
  }
}

export const setSessionDismissed = (
  viewId: string,
  userId: string | null | undefined,
): void => {
  if (!userId) return
  try {
    sessionStorage.setItem(keyFor(viewId, userId), '1')
  } catch {
    // Storage unavailable — the server state still applies on the next fetch,
    // so only `once_per_session` degrades, and it degrades to showing again,
    // which is the safe direction.
  }
}
